import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import { Currency } from 'src/components/atoms/Currency'
import { DefinitionListItem } from 'src/components/atoms/DefinitionListItem'
import { DownloadAttachmentButton } from 'src/components/molecules/DownloadAttachmentButton'
import { Table } from 'src/components/molecules/Table'

import { ListAccountPurchaseReceiptsQuery } from 'src/config/generated/graphql'
import useMediaQuery from 'src/hooks/useMediaQuery'
import { getReceiptAttachmentTypesForPurchase } from 'src/types/attachment'
import { ArrayElement } from 'src/types/common'
import {
  PURCHASE_TYPE_VALUES,
  PurchaseState,
  PurchaseType
} from 'src/types/purchase'
import { formatDate } from 'src/utils/date'

type AccountRecurringReceipts = ArrayElement<
  ListAccountPurchaseReceiptsQuery['Purchase']
>

const paymentMethodMap: { [key: number]: string } = {
  1: 'Credit card',
  2: 'Invoice'
}

type AccountReceiptsTableProps = {
  data: ListAccountPurchaseReceiptsQuery['Purchase']
}

const AccountReceiptsTable = ({ data }: AccountReceiptsTableProps) => {
  const columnHelper = createColumnHelper<AccountRecurringReceipts>()
  const isMobile = useMediaQuery('(max-width: 1024px)')

  // Helper function to find the correct receipt/invoice attachment for a purchase
  const findReceiptAttachment = (purchase: AccountRecurringReceipts) => {
    if (!purchase.Attachment?.length) return null

    const expectedTypes = getReceiptAttachmentTypesForPurchase(purchase.Type)
    const matchingAttachments = purchase.Attachment.filter(a =>
      expectedTypes.includes(a.Type)
    )

    if (matchingAttachments.length === 0) return null

    // For invoice payment method (PaymentMethod = 2), prefer InvoiceGiftTree (8) or InvoiceFundPlanting (7)
    // PaymentMethod enum: 1 = CreditCard, 2 = Invoice, 3 = GrabOne
    if (purchase.PaymentMethod === 2) {
      const invoice = matchingAttachments.find(
        a => a.Type === 8 || a.Type === 7
      )
      if (invoice) return invoice
    }

    // Otherwise return the first matching attachment
    return matchingAttachments[0]
  }

  // Render receipt download button or "no attachment" message
  const renderReceiptDownload = (purchase: AccountRecurringReceipts) => {
    const attachment = findReceiptAttachment(purchase)
    return attachment ? (
      <DownloadAttachmentButton
        attachmentType={attachment.Type}
        attachmentId={attachment.Id}
        text='Download'
      />
    ) : (
      <div className='text-gray-500 text-sm'>No attachment</div>
    )
  }

  const mobileColumns: ColumnDef<AccountRecurringReceipts, any>[] = [
    columnHelper.accessor('Id', {
      header: '',
      cell: ({ row: { original } }) => (
        <div className='flex flex-col text-left'>
          <dl className='mb-3 pb-2'>
            <DefinitionListItem
              title='Date'
              data={
                original?.CreatedOn
                  ? formatDate({
                      date: original?.CreatedOn,
                      format: 'DD MMM YYYY',
                      isUtc: true
                    })
                  : ''
              }
            />
            <DefinitionListItem title='Reference' data={original?.Reference} />
            <DefinitionListItem
              title='Method'
              data={
                original?.PaymentMethod
                  ? paymentMethodMap[original?.PaymentMethod]
                  : null
              }
            />
            <DefinitionListItem
              title='Type'
              data={
                original?.SubscriptionId
                  ? `${
                      PURCHASE_TYPE_VALUES[original?.Type as PurchaseType]
                    } (Monthly)`
                  : PURCHASE_TYPE_VALUES[original?.Type as PurchaseType]
              }
            />
            <DefinitionListItem
              title='Total'
              data={
                original?.Total ? <Currency amount={original?.Total} /> : null
              }
            />
            <DefinitionListItem
              title='Status'
              data={original?.State ? PurchaseState[original?.State] : null}
            />
            <DefinitionListItem title='Email' data={original?.ReceiptEmail} />
            <DefinitionListItem
              title='Receipt'
              data={renderReceiptDownload(original)}
            />
          </dl>
        </div>
      )
    })
  ]

  const desktopColumns: ColumnDef<AccountRecurringReceipts, any>[] = [
    columnHelper.accessor('Reference', {
      header: 'Ref',
      cell: props => props.getValue(),
      meta: {
        align: 'right'
      },
      size: 100
    }),
    columnHelper.accessor('CreatedOn', {
      header: 'Date',
      cell: props =>
        formatDate({
          date: props.getValue(),
          format: 'DD MMM YYYY',
          isUtc: true
        }),
      size: 120
    }),
    columnHelper.accessor('PaymentMethod', {
      header: 'Method',
      cell: props => paymentMethodMap[props.getValue()],
      size: 100
    }),
    columnHelper.accessor('Type', {
      header: 'Type',
      cell: props =>
        props.row.original?.SubscriptionId
          ? `${
              PURCHASE_TYPE_VALUES[props.row.original?.Type as PurchaseType]
            } (Monthly)`
          : PURCHASE_TYPE_VALUES[props.getValue() as PurchaseType],
      size: 80
    }),
    columnHelper.accessor('Total', {
      header: 'Total',
      cell: props => <Currency amount={props.getValue()} />,
      meta: {
        align: 'right'
      },
      size: 100
    }),
    columnHelper.accessor('State', {
      header: 'Status',
      cell: props => PurchaseState[props.getValue()],
      size: 100
    }),
    columnHelper.accessor('ReceiptEmail', {
      header: 'Email'
    }),
    columnHelper.accessor('Id', {
      header: 'Receipt',
      cell: props => renderReceiptDownload(props.row.original),
      enableSorting: false,
      meta: {
        align: 'center'
      }
    })
  ]
  return (
    <Table
      headerClasses={accessor =>
        accessor === 'Purchase_CreatedOn' ? 'text-left!' : ''
      }
      cellClasses={accessor =>
        accessor === 'Purchase_CreatedOn'
          ? 'text-left!  py-[10px]!'
          : ' py-[10px]!'
      }
      data={data || []}
      columns={isMobile ? mobileColumns : desktopColumns}
      noResultsText="You haven't made any donation payments yet."
    />
  )
}

export default AccountReceiptsTable
